import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';

interface DeletionView {
  appLine: string;
  intro: string;
  stepsTitle: string;
  steps: string[];
  mailSubject: string;
  mailBody: string;
  mailCta: string;
  delayTitle: string;
  delayBody: string;
  deletedTitle: string;
  deleted: string[];
  keptTitle: string;
  kept: string[];
  keptNote: string;
  contactTitle: string;
  contactBody: string;
  contactEmail: string;
  lastUpdated: string;
}

/**
 * Page publique de demande de suppression de compte — exigée par Google Play
 * (« URL de suppression de compte » de la fiche Store). Elle doit nommer l'appli
 * et l'éditeur, décrire clairement la procédure, et lister ce qui est supprimé,
 * ce qui est conservé et pendant combien de temps.
 *
 * Contenu statique (pas de Firestore) : la page doit rester lisible par le
 * relecteur Google même hors connexion / sans compte. La langue suit le
 * LOCALE_ID du build, comme PrivacyPolicyComponent.
 */
@Component({
  selector: 'app-delete-account',
  templateUrl: './delete-account.component.html',
  styleUrls: ['./delete-account.component.scss'],
})
export class DeleteAccountComponent implements OnInit {
  view: DeletionView;

  constructor(@Inject(LOCALE_ID) locale: string) {
    const isFr = (locale || 'en-US').toLowerCase().startsWith('fr');
    this.view = isFr ? DEFAULT_FR : DEFAULT_EN;
  }

  ngOnInit(): void {
    this.goToTop();
  }

  goToTop(): void {
    const el = document.getElementById('goToTop');
    if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
  }

  /** Lien mailto pré-rempli (objet + corps) pour la demande de suppression. */
  get mailtoLink(): string {
    return 'mailto:' + this.view.contactEmail
      + '?subject=' + encodeURIComponent(this.view.mailSubject)
      + '&body=' + encodeURIComponent(this.view.mailBody);
  }
}

// ── Contenu (EN / FR) ────────────────────────────────────────────────────────

const CONTACT_EMAIL = 'privacy@earlytransfert.com';

const DEFAULT_EN: DeletionView = {
  appLine: 'Early Transfert — published by Vofack Consultation Inc.',
  intro: 'You can ask us at any time to delete your Early Transfert account and the data '
    + 'associated with it. This page explains exactly how to make that request, what we delete, '
    + 'and what we are legally required to keep.',
  stepsTitle: 'How to request the deletion of your account',
  steps: [
    'Send an email to ' + CONTACT_EMAIL + ' from the email address of your Early Transfert '
      + 'account (this is how we verify the request comes from you).',
    'Use the subject line: "Account deletion request".',
    'In the message, include your first and last name, and the phone number registered on the '
      + 'account.',
    'We reply within 5 business days to confirm your identity, and we may ask you to complete '
      + 'or cancel any transfer still in progress.',
    'Once confirmed, your account is deactivated immediately and the deletion is completed '
      + 'within 30 days.',
  ],
  mailSubject: 'Account deletion request',
  mailBody: 'Hello,\n\n'
    + 'I request the deletion of my Early Transfert account and of the data associated with it.\n\n'
    + 'First and last name: \n'
    + 'Account email: \n'
    + 'Registered phone number: \n\n'
    + 'Thank you.',
  mailCta: 'Send my deletion request',
  delayTitle: 'Processing time',
  delayBody: 'Your account is deactivated as soon as we have verified your identity. Deletion of '
    + 'your data is completed within 30 days of that confirmation. Backups are rotated out within '
    + '90 days at the latest.',
  deletedTitle: 'Data that is deleted',
  deleted: [
    'Your account and login credentials (email, password, authentication session).',
    'Your profile: first and last name, phone number, address, country, town, date of birth, '
      + 'occupation.',
    'Your beneficiaries and the deposit details you saved (Interac email, Mobile Money number).',
    'Your wallet balance, after any remaining funds have been paid out to you.',
    'Your posts, offers, likes and in-app messages.',
    'Your device identifiers and push-notification tokens.',
  ],
  keptTitle: 'Data that is kept, and for how long',
  kept: [
    'Transaction records (amounts, dates, exchange rates, counterparties) and the identity '
      + 'verification (KYC) documents attached to them: kept for at least 5 years after the last '
      + 'transaction, as required by Canadian anti-money-laundering law (PCMLTFA) and by our '
      + 'payment partners. They are deleted once that period expires.',
    'Accounting and tax records (invoices, fees charged): kept for 6 years, as required by tax law.',
    'Records of fraud, chargebacks or abuse: kept for up to 5 years to protect other users and to '
      + 'prevent the banned account from being recreated.',
    'Anonymised, aggregated statistics that can no longer be linked to you: kept without time limit.',
  ],
  keptNote: 'The data listed above is kept only to meet a legal obligation or to prevent fraud. '
    + 'It is never used to contact you again for commercial purposes, and it is deleted as soon as '
    + 'the retention period ends.',
  contactTitle: 'Any question?',
  contactBody: 'For any question about this procedure, contact Vofack Consultation Inc. at '
    + CONTACT_EMAIL + '.',
  contactEmail: CONTACT_EMAIL,
  lastUpdated: 'Last updated: August 13, 2026',
};

const DEFAULT_FR: DeletionView = {
  appLine: 'Early Transfert — édité par Vofack Consultation Inc.',
  intro: "Vous pouvez à tout moment nous demander de supprimer votre compte Early Transfert et les "
    + 'données qui y sont associées. Cette page explique exactement comment faire cette demande, ce '
    + 'que nous supprimons, et ce que la loi nous oblige à conserver.',
  stepsTitle: 'Comment demander la suppression de votre compte',
  steps: [
    'Envoyez un e-mail à ' + CONTACT_EMAIL + " depuis l'adresse e-mail de votre compte Early "
      + "Transfert (c'est ainsi que nous vérifions que la demande vient bien de vous).",
    'Indiquez comme objet : « Demande de suppression de compte ».',
    'Dans le message, précisez vos nom et prénom, ainsi que le numéro de téléphone enregistré sur '
      + 'le compte.',
    'Nous répondons sous 5 jours ouvrés pour confirmer votre identité, et nous pouvons vous '
      + "demander de finaliser ou d'annuler un transfert encore en cours.",
    'Une fois la confirmation faite, votre compte est désactivé immédiatement et la suppression '
      + 'est achevée sous 30 jours.',
  ],
  mailSubject: 'Demande de suppression de compte',
  mailBody: 'Bonjour,\n\n'
    + 'Je demande la suppression de mon compte Early Transfert et des données associées.\n\n'
    + 'Nom et prénom : \n'
    + 'E-mail du compte : \n'
    + 'Numéro de téléphone enregistré : \n\n'
    + 'Merci.',
  mailCta: 'Envoyer ma demande de suppression',
  delayTitle: 'Délai de traitement',
  delayBody: 'Votre compte est désactivé dès que nous avons vérifié votre identité. La suppression '
    + 'de vos données est achevée dans les 30 jours suivant cette confirmation. Les sauvegardes '
    + 'sont purgées dans un délai maximal de 90 jours.',
  deletedTitle: 'Données supprimées',
  deleted: [
    'Votre compte et vos identifiants de connexion (e-mail, mot de passe, session '
      + "d'authentification).",
    'Votre profil : nom, prénom, numéro de téléphone, adresse, pays, ville, date de naissance, '
      + 'profession.',
    'Vos bénéficiaires et les informations de dépôt enregistrées (e-mail Interac, numéro Mobile '
      + 'Money).',
    'Le solde de votre wallet, une fois les fonds restants reversés.',
    "Vos annonces, vos offres, vos mentions « j'aime » et vos messages dans l'application.",
    "Les identifiants de votre appareil et vos jetons de notifications push.",
  ],
  keptTitle: 'Données conservées, et pendant combien de temps',
  kept: [
    'Les enregistrements de transactions (montants, dates, taux de change, contreparties) et les '
      + "documents de vérification d'identité (KYC) qui s'y rattachent : conservés au moins 5 ans "
      + 'après la dernière transaction, comme l\'exigent la loi canadienne de lutte contre le '
      + 'blanchiment (LRPCFAT) et nos partenaires de paiement. Ils sont supprimés à l\'expiration '
      + 'de ce délai.',
    'Les pièces comptables et fiscales (factures, frais facturés) : conservées 6 ans, comme '
      + "l'exige la législation fiscale.",
    "Les signalements de fraude, de rétrofacturation ou d'abus : conservés jusqu'à 5 ans afin de "
      + 'protéger les autres utilisateurs et d\'empêcher la recréation d\'un compte banni.',
    'Des statistiques anonymisées et agrégées, qui ne permettent plus de vous identifier : '
      + 'conservées sans limite de durée.',
  ],
  keptNote: 'Les données ci-dessus ne sont conservées que pour respecter une obligation légale ou '
    + 'prévenir la fraude. Elles ne sont jamais utilisées pour vous recontacter à des fins '
    + "commerciales, et elles sont supprimées dès la fin du délai de conservation.",
  contactTitle: 'Une question ?',
  contactBody: 'Pour toute question sur cette procédure, contactez Vofack Consultation Inc. à '
    + CONTACT_EMAIL + '.',
  contactEmail: CONTACT_EMAIL,
  lastUpdated: 'Dernière mise à jour : 13 août 2026',
};
